import { Check, FileCheck2, X } from 'lucide-react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type {
  CartLineCustomizationFile,
  Customization,
  Media,
} from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaThumb, MediaUploadButton, useMediaList, useMediaUploader } from '@/features/media';

import { acceptedTypes } from '../schemas/customization-rules';

const PAGE_SIZE = 12;

const nameOf = (media: Pick<Media, 'alt' | 'filename'>, fallback: string) =>
  media.filename ?? media.alt ?? fallback;

export interface FileCustomizationPickerProps {
  def: Customization;
  /** A picked media id, the id of `kept`, or '' for none. */
  value: string;
  onChange: (value: string) => void;
  /** The file already on the line; sending its id back keeps it. */
  kept?: CartLineCustomizationFile | null;
  invalid?: boolean;
}

/**
 * Answers a file customization from the organisation's media library: staff have no shopper
 * upload token, so the backend copies the picked file into one. Files of a type the
 * customization does not accept are shown but cannot be picked, and an upload is limited to
 * those types, so the backend's 422 on the content type is not the first time anyone hears of it.
 */
export function FileCustomizationPicker({
  def,
  value,
  onChange,
  kept = null,
  invalid = false,
}: FileCustomizationPickerProps) {
  const { t } = useTranslation('draftOrders');
  const accept = acceptedTypes(def);
  const [picked, setPicked] = useState<Media | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const query = useMediaList({ limit: PAGE_SIZE });
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  const choose = useCallback(
    (media: Media) => {
      if (!accept.includes(media.mimeType)) {
        setProblem(t('fileField.wrongType', { name: nameOf(media, t('fileField.untitled')) }));
        return;
      }
      setProblem(null);
      setPicked(media);
      onChange(media.id);
    },
    [accept, onChange, t],
  );
  // The upload queue is rebuilt whenever its callback changes, which would drop an upload in
  // flight; the callback stays stable and reads the latest handler through a ref.
  const latestChoose = useRef(choose);
  useLayoutEffect(() => {
    latestChoose.current = choose;
  });
  const onUploaded = useCallback((media: Media) => latestChoose.current(media), []);
  const uploader = useMediaUploader({ onUploaded });
  const uploading = uploader.items.filter(
    (item) => item.status === 'uploading' || item.status === 'queued',
  );
  const failed = uploader.items.filter((item) => item.status === 'error');

  const onFiles = (files: File[]) => {
    const [file] = files;
    if (!file) return;
    if (!accept.includes(file.type)) {
      setProblem(t('fileField.wrongType', { name: file.name }));
      return;
    }
    setProblem(null);
    uploader.add([file]);
  };

  const keptSelected = kept !== null && value === kept.id;
  const selected =
    picked?.id === value ? picked : (items.find((media) => media.id === value) ?? null);

  return (
    <div
      className={cn('flex flex-col gap-3 rounded-md border p-3', invalid && 'border-destructive')}
      role="group"
      aria-label={def.label}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm" aria-live="polite">
          {keptSelected ? (
            <span className="flex items-center gap-1.5">
              <FileCheck2 className="size-4" aria-hidden />
              {t('fileField.current', { name: kept.filename })}
            </span>
          ) : value ? (
            t('fileField.selected', {
              name: selected ? nameOf(selected, t('fileField.untitled')) : t('fileField.untitled'),
            })
          ) : (
            <span className="text-muted-foreground">{t('fileField.none')}</span>
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          {value && !def.isRequired ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={t('fileField.clear', { label: def.label })}
              onClick={() => onChange('')}
            >
              <X aria-hidden />
              {t('fileField.clearShort')}
            </Button>
          ) : null}
          {kept && !keptSelected ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange(kept.id)}>
              {t('fileField.keepCurrent')}
            </Button>
          ) : null}
          <MediaUploadButton
            variant="outline"
            size="sm"
            multiple={false}
            accept={accept}
            onFiles={onFiles}
            aria-label={t('fileField.upload', { label: def.label })}
          >
            {t('fileField.uploadShort')}
          </MediaUploadButton>
        </div>
      </div>
      <p className="text-muted-foreground text-xs">
        {t('fileField.accepts', { types: accept.join(', ') })}
      </p>
      {problem ? (
        <p role="alert" className="text-destructive text-sm">
          {problem}
        </p>
      ) : null}
      {uploading.map((item) => (
        <p key={item.id} className="text-muted-foreground text-xs">
          {t('fileField.uploading', { name: item.file.name })}
        </p>
      ))}
      {failed.map((item) => (
        <p key={item.id} className="text-destructive text-xs">
          {t('fileField.uploadFailed', { name: item.file.name })}
        </p>
      ))}
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending ? (
        <div className="grid grid-cols-4 gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square w-full rounded-md" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
          {t('fileField.empty')}
        </p>
      ) : (
        <ul
          aria-label={t('fileField.library', { label: def.label })}
          className="grid max-h-48 grid-cols-4 gap-2 overflow-y-auto p-0.5"
        >
          {items.map((media) => {
            const isSelected = media.id === value;
            const accepted = accept.includes(media.mimeType);
            const name = nameOf(media, t('fileField.untitled'));
            return (
              <li key={media.id}>
                <button
                  type="button"
                  aria-pressed={isSelected}
                  aria-disabled={!accepted}
                  aria-label={accepted ? name : t('fileField.notAccepted', { name })}
                  onClick={() => choose(media)}
                  className={cn(
                    'focus-visible:ring-ring/50 relative block w-full rounded-md text-left outline-none focus-visible:ring-[3px]',
                    !accepted && 'cursor-not-allowed opacity-40',
                  )}
                >
                  <MediaThumb
                    media={media}
                    className={cn('w-full border', isSelected && 'ring-primary ring-2')}
                  />
                  {isSelected ? (
                    <span className="bg-primary text-primary-foreground absolute top-1 right-1 flex size-5 items-center justify-center rounded-full">
                      <Check className="size-3" aria-hidden />
                    </span>
                  ) : null}
                  <span className="mt-1 block truncate text-[11px]" aria-hidden>
                    {name}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {query.hasNextPage ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-center"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          {t('fileField.loadMore')}
        </Button>
      ) : null}
    </div>
  );
}
